import React, { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { BrowserRouter as Router, Route, Switch, Redirect, useLocation } from 'react-router-dom';
import {
    SESSION_EXPIRED_EVENT,
    SESSION_RESTORED_EVENT,
    getCurrentUser,
    getStoredUser,
    getToken,
    isAuthenticated,
    notifySessionExpired,
    shouldKeepRouteMounted,
    startSessionKeepAlive,
} from './utils/auth';
import { currencyRatesAPI } from './services/api';
import { setExchangeRates } from './utils/currencyUtils';
import { canAccessPath } from './utils/roles';
import { autoEnablePushNotifications } from './utils/browserNotifications';

// Layout components
import Header from './components/Layout/Header';
import Sidebar from './components/Layout/Sidebar';
import BottomNav from './components/Layout/BottomNav';
import { PHONE_NAV_MEDIA_QUERY } from './components/Layout/phoneLayout';
import useMatchMedia from './hooks/useMatchMedia';
import NotificationPermissionPrompt from './components/Notifications/NotificationPermissionPrompt';
import ReloginModal from './components/common/ReloginModal';
import { OfflineBanner } from './components/common/Banners';
import RouteErrorBoundary from './components/common/RouteErrorBoundary';
import ToastHost from './components/common/Toast';
import { ConfirmHost } from './components/common/useConfirm';
import { PageSkeleton } from './components/common/Skeleton';
import lazyWithRetry from './utils/lazyWithRetry';

import './App.css';

// Маршруты загружаются по мере надобности
import UploadQueueIndicator from './components/Orders/UploadQueueIndicator';

const Login = lazyWithRetry(() => import('./components/Auth/Login'));
const ClientStatus = lazyWithRetry(() => import('./pages/ClientStatus'));
const Dashboard = lazyWithRetry(() => import('./pages/Dashboard'));
const MyDay = lazyWithRetry(() => import('./components/MyDay/MyDay'));
const ClientList = lazyWithRetry(() => import('./components/Clients/ClientList'));
const ClientForm = lazyWithRetry(() => import('./components/Clients/ClientForm'));
const ClientDetails = lazyWithRetry(() => import('./components/Clients/ClientDetails'));
const VehicleList = lazyWithRetry(() => import('./components/Vehicles/VehicleList'));
const VehicleForm = lazyWithRetry(() => import('./components/Vehicles/VehicleForm'));
const VehicleDetails = lazyWithRetry(() => import('./components/Vehicles/VehicleDetails'));
const OrderList = lazyWithRetry(() => import('./components/Orders/OrderList'));
const OrderForm = lazyWithRetry(() => import('./components/Orders/OrderForm'));
const OrderDetails = lazyWithRetry(() => import('./components/Orders/OrderDetails'));
const CompletedOrders = lazyWithRetry(() => import('./components/Orders/CompletedOrders'));
const ServiceList = lazyWithRetry(() => import('./components/Services/ServiceList'));
const ServiceForm = lazyWithRetry(() => import('./components/Services/ServiceForm'));
const UserList = lazyWithRetry(() => import('./components/Users/UserList'));
const UserForm = lazyWithRetry(() => import('./components/Users/UserForm'));
const Analytics = lazyWithRetry(() => import('./components/Analytics/Analytics'));
const Materials = lazyWithRetry(() => import('./components/Materials/Materials'));
const Schedule = lazyWithRetry(() => import('./components/Schedule/Schedule'));
const Accounting = lazyWithRetry(() => import('./components/Accounting/Accounting'));
const ExpenseCategories = lazyWithRetry(() => import('./components/Accounting/ExpenseCategories'));
const AccountingSheet = lazyWithRetry(() => import('./components/AccountingSheet/AccountingSheet'));
const TemplateEditor = lazyWithRetry(() => import('./components/AccountingSheet/TemplateEditor'));
const DealershipManagement = lazyWithRetry(() => import('./components/Dealerships/DealershipManagement'));
const DealershipDetails = lazyWithRetry(() => import('./components/Dealerships/DealershipDetails'));
const SimpleCurrencyRates = lazyWithRetry(() => import('./components/Settings/SimpleCurrencyRates'));
const NotificationSettings = lazyWithRetry(() => import('./components/Settings/NotificationSettings'));

const RouteFallback = () => <PageSkeleton />;

const NotificationInitializer = () => {
    const location = useLocation();
    const synchronizedTokenRef = useRef(null);

    useEffect(() => {
        if (!isAuthenticated()) {
            synchronizedTokenRef.current = null;
            return undefined;
        }

        const token = localStorage.getItem('token');
        if (!token || synchronizedTokenRef.current === token) {
            return undefined;
        }

        let isCancelled = false;
        const timeoutId = window.setTimeout(async () => {
            try {
                if (!isCancelled) {
                    await autoEnablePushNotifications();
                    synchronizedTokenRef.current = token;
                }
            } catch (error) {
                console.error('Не удалось синхронизировать push-уведомления:', error);
            }
        }, 1200);

        return () => {
            isCancelled = true;
            window.clearTimeout(timeoutId);
        };
    }, [location.pathname]);

    return null;
};

// Protected Route component
const ProtectedRoute = ({ component: Component, ...rest }) => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [currentUser, setCurrentUser] = useState(getStoredUser());
    const [loadingUser, setLoadingUser] = useState(!getStoredUser());
    const [, forceSessionRender] = useState(0);
    const showBottomNav = useMatchMedia(PHONE_NAV_MEDIA_QUERY);

    // Токен мог истечь без единого запроса: открываем окно входа, форма остаётся на месте
    useEffect(() => {
        if (!isAuthenticated() && getToken()) {
            notifySessionExpired();
        }
    });

    useEffect(() => {
        const rerender = () => forceSessionRender((value) => value + 1);
        window.addEventListener(SESSION_EXPIRED_EVENT, rerender);
        window.addEventListener(SESSION_RESTORED_EVENT, rerender);
        return () => {
            window.removeEventListener(SESSION_EXPIRED_EVENT, rerender);
            window.removeEventListener(SESSION_RESTORED_EVENT, rerender);
        };
    }, []);

    const handleToggleSidebar = () => {
        setIsSidebarOpen(prev => !prev);
    };

    const handleCloseSidebar = () => {
        setIsSidebarOpen(false);
    };

    useEffect(() => {
        if (currentUser) {
            setLoadingUser(false);
            return;
        }

        let isMounted = true;
        getCurrentUser().then((user) => {
            if (isMounted) {
                setCurrentUser(user);
                setLoadingUser(false);
            }
        });

        return () => {
            isMounted = false;
        };
    }, [currentUser]);

    const role = (currentUser?.role || '').toLowerCase();

    const canAccess = useMemo(() => canAccessPath(role, rest.path), [role, rest.path]);

    return (
        <Route
            {...rest}
            render={(props) =>
                isAuthenticated() || shouldKeepRouteMounted() ? (
                    loadingUser ? (
                        <div className="loading">Загрузка...</div>
                    ) : canAccess ? (
                    <div className={`app-layout${showBottomNav ? ' has-bottom-nav' : ''}`}>
                        <Sidebar
                            isOpen={isSidebarOpen}
                            onClose={handleCloseSidebar}
                            role={role}
                        />
                        <div
                            className={`sidebar-backdrop${isSidebarOpen ? ' show' : ''}`}
                            onClick={handleCloseSidebar}
                            role="presentation"
                        />
                        <div className="main-layout">
                            <Header onMenuToggle={handleToggleSidebar} />
                            <main className="content-area">
                                <RouteErrorBoundary resetKey={props.location.pathname}>
                                    <Suspense fallback={<RouteFallback />}>
                                        <Component {...props} />
                                    </Suspense>
                                </RouteErrorBoundary>
                            </main>
                        </div>
                        {showBottomNav && (
                            <BottomNav
                                role={role}
                                isMenuOpen={isSidebarOpen}
                                onMenuToggle={handleToggleSidebar}
                            />
                        )}
                    </div>
                    ) : (
                        <Redirect to="/dashboard" />
                    )
                ) : (
                    <Redirect to="/login" />
                )
            }
        />
    );
};

// Public Route component (redirect to dashboard if already authenticated)
const PublicRoute = ({ component: Component, ...rest }) => (
    <Route
        {...rest}
        render={(props) =>
            !isAuthenticated() ? (
                <RouteErrorBoundary resetKey={props.location.pathname}>
                    <Suspense fallback={<RouteFallback />}>
                        <Component {...props} />
                    </Suspense>
                </RouteErrorBoundary>
            ) : (
                <Redirect to="/dashboard" />
            )
        }
    />
);

function App() {
    useEffect(() => startSessionKeepAlive(), []);

    useEffect(() => {
        const loadCurrentRates = async () => {
            if (!isAuthenticated()) return;
            try {
                const rates = await currencyRatesAPI.getCurrent();
                setExchangeRates(rates || {});
            } catch (error) {
                console.error('Не удалось загрузить актуальные курсы валют:', error);
            }
        };

        loadCurrentRates();
    }, []);

    return (
        <Router>
            <div className="App">
                <NotificationInitializer />
                <NotificationPermissionPrompt />
                <OfflineBanner />
                <ReloginModal />
                <ConfirmHost />
                <ToastHost />
                <UploadQueueIndicator />
                <Switch>
                    {/* Public routes */}
                    <PublicRoute path="/login" component={Login} />
                    {/* Статус заказа для клиента: открывается без входа по подписанной ссылке */}
                    <Route exact path="/s/:token" component={ClientStatus} />
                    
                    {/* Protected routes */}
                    <ProtectedRoute exact path="/" component={Dashboard} />
                    <ProtectedRoute exact path="/dashboard" component={Dashboard} />
                    <ProtectedRoute exact path="/my-day" component={MyDay} />
                    
                    {/* Client routes */}
                    <ProtectedRoute exact path="/clients" component={ClientList} />
                    <ProtectedRoute exact path="/clients/new" component={ClientForm} />
                    <ProtectedRoute exact path="/clients/:id/edit" component={ClientForm} />
                    <ProtectedRoute exact path="/clients/:id" component={ClientDetails} />
                    
                    {/* Vehicle routes */}
                    <ProtectedRoute exact path="/vehicles" component={VehicleList} />
                    <ProtectedRoute exact path="/vehicles/new" component={VehicleForm} />
                    <ProtectedRoute exact path="/vehicles/:id/edit" component={VehicleForm} />
                    <ProtectedRoute exact path="/vehicles/:id" component={VehicleDetails} />
                    
                    {/* Order routes */}
                    <ProtectedRoute exact path="/orders" component={OrderList} />
                    <ProtectedRoute exact path="/orders/completed" component={CompletedOrders} />
                    <ProtectedRoute exact path="/orders/new" component={OrderForm} />
                    <ProtectedRoute exact path="/orders/:id/edit" component={OrderForm} />
                    <ProtectedRoute exact path="/orders/:id" component={OrderDetails} />
                    
                    {/* Service routes */}
                    <ProtectedRoute exact path="/services" component={ServiceList} />
                    <ProtectedRoute exact path="/services/new" component={ServiceForm} />
                    <ProtectedRoute exact path="/services/:id/edit" component={ServiceForm} />
                    
                    {/* User routes */}
                    <ProtectedRoute path="/users" exact component={UserList} />
                    <ProtectedRoute path="/users/new" exact component={UserForm} />
                    <ProtectedRoute path="/users/:id" exact component={UserForm} />
                    <ProtectedRoute path="/users/:id/edit" exact component={UserForm} />
                    
                    {/* Analytics routes */}
                    <ProtectedRoute exact path="/analytics" component={Analytics} />
                    <ProtectedRoute exact path="/schedule" component={Schedule} />
                    <ProtectedRoute exact path="/materials" component={Materials} />
                    <ProtectedRoute exact path="/dealerships" component={DealershipManagement} />
                    <ProtectedRoute exact path="/dealerships/:id" component={DealershipDetails} />
                    <ProtectedRoute exact path="/accounting" component={AccountingSheet} />
                    <ProtectedRoute exact path="/accounting/legacy" component={Accounting} />
                    <ProtectedRoute exact path="/accounting/template" component={TemplateEditor} />
                    <ProtectedRoute exact path="/accounting/expenses" component={ExpenseCategories} />
                    
                    {/* Settings routes */}
                    <ProtectedRoute exact path="/settings/currency-rates" component={SimpleCurrencyRates} />
                    <ProtectedRoute exact path="/settings/notifications" component={NotificationSettings} />
                    
                    {/* Default redirect */}
                    <Route render={() => <Redirect to="/dashboard" />} />
                </Switch>
            </div>
        </Router>
    );
}

export default App;